<template>
    <b-modal :id="`send-doc-modal-${docId}`" ref="modal" hide-footer :title="`Send ${docType}`">
        <p class="text-muted mb-3">
            Send {{ docType.toLowerCase() }} <strong>{{ number }}</strong> to your customer.
        </p>

        <div class="form-group">
            <label class="small mb-1">Recipient phone number</label>
            <vue-tel-input v-on:validate="validatePhone" v-model="phone"></vue-tel-input>
            <small v-if="phoneValidMessage" class="text-danger d-block mt-1">{{ phoneValidMessage }}</small>
        </div>

        <div class="d-flex mt-2" style="gap:8px;">
            <button type="button" class="btn btn-outline-success flex-fill" :disabled="!phone || !!phoneValidMessage || !shareUrl" @click="sendViaWhatsApp">
                <i class="bi bi-whatsapp mr-1"></i> WhatsApp
            </button>
            <button type="button" class="btn btn-outline-primary flex-fill" :disabled="!phone || !!phoneValidMessage || sendingSms || !shareUrl" @click="sendViaSms">
                <span v-if="sendingSms" class="spinner-border spinner-border-sm mr-1"></span>
                <i v-else class="bi bi-chat-dots mr-1"></i> SMS
            </button>
        </div>

        <hr>

        <button type="button" class="btn btn-outline-secondary btn-block" :disabled="downloading" @click="download">
            <span v-if="downloading" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-file-earmark-pdf mr-1"></i> Download PDF
        </button>

        <small v-if="error" class="text-danger d-block mt-2">{{ error }}</small>
    </b-modal>
</template>

<script>
import { downloadPdf } from '../../services/pdfDownload';

export default {
    name: 'SendDocumentModal',
    props: {
        docType: { type: String, required: true }, // 'Quote' | 'Invoice'
        docId: { type: String, required: true },
        number: { type: String, default: '' },
        customer: { type: Object, default: null },
    },
    data() {
        return {
            phone: '',
            phoneValidMessage: '',
            sendingSms: false,
            downloading: false,
            error: '',
            // Read-only customer link (no login), from /share-link.
            shareUrl: '',
        };
    },
    computed: {
        endpoint() {
            return this.docType === 'Quote' ? 'quotes' : 'invoices';
        },
        message() {
            return `Here's your ${this.docType.toLowerCase()} ${this.number}: ${this.shareUrl}`;
        },
    },
    methods: {
        open() {
            this.phone = (this.customer && this.customer.phone) || '';
            this.phoneValidMessage = '';
            this.error = '';
            this.$refs.modal.show();
            this.loadShareUrl();
        },
        async loadShareUrl() {
            this.shareUrl = '';
            try {
                const { data } = await axios.get(`/api/${this.endpoint}/${this.docId}/share-link`);
                this.shareUrl = data.url;
            } catch (e) {
                this.error = 'Could not create the customer link. Please try again.';
            }
        },
        validatePhone(phoneObject) {
            this.phoneValidMessage = (phoneObject.number && !phoneObject.valid) ? 'Enter a valid phone number' : '';
        },
        normalizedPhone() {
            return this.phone.startsWith('0')
                ? '254' + this.phone.substring(1)
                : this.phone.replace(/^\+/, '').replace(/\s/g, '');
        },
        markSent() {
            axios.post(`/api/${this.endpoint}/${this.docId}/mark-sent`).then(({ data }) => {
                this.$emit('sent', data.sent_at);
            }).catch(() => {});
        },
        sendViaWhatsApp() {
            window.open(`https://wa.me/${this.normalizedPhone()}?text=${encodeURIComponent(this.message)}`, '_blank');
            this.markSent();
        },
        async sendViaSms() {
            this.sendingSms = true;
            this.error = '';
            try {
                await axios.post('/api/send-sms-to-customer', {
                    customer: { phone: this.phone },
                    message: this.message,
                });
                this.$toasts.push({ type: 'success', message: `${this.docType} sent via SMS.` });
                this.markSent();
                this.$refs.modal.hide();
            } catch (e) {
                this.error = (e.response && e.response.data && e.response.data.message) || 'Could not send SMS. Please try again.';
            } finally {
                this.sendingSms = false;
            }
        },
        async download() {
            this.downloading = true;
            this.error = '';
            try {
                await downloadPdf(`/api/${this.endpoint}/${this.docId}/pdf`, `${this.docType}-${this.number}.pdf`);
            } catch (e) {
                this.error = 'Could not download the PDF. Please try again.';
            } finally {
                this.downloading = false;
            }
        },
    },
};
</script>
